import { Timestamp } from '@firebase/firestore';

import type { IntakeStatusBaseSteps } from '@shared/patient-intake-status';

import { parseName } from '../../utils';
import { buildGenerateConsentToReleaseHealthSummaryStep } from './buildGenerateConsentToReleaseHealthSummaryStep.ts';

const hasMedicareCardOrIHI = (userData: Record<string, unknown>) => {
  if (!userData?.concessionCards) {
    return false;
  }

  if (typeof userData.concessionCards !== 'object') {
    return false;
  }

  if ('medicare card' in userData.concessionCards) {
    return !!userData.concessionCards['medicare card'];
  }

  if ('ihi' in userData.concessionCards) {
    return !!userData.concessionCards['ihi'];
  }

  return false;
};

export const getDefaultBaseVerificationIntakeStatus = (userData: Record<string, unknown>): IntakeStatusBaseSteps => {
  const { firstName, lastName, email, id } = userData;

  const name = parseName((firstName as string) ?? '', (lastName as string) ?? '');

  const defaultActionedBy = {
    name,
    id: (id as string) ?? '',
    email: (email as string) ?? '',
    userType: 'patient',
  } satisfies IntakeStatusBaseSteps[keyof IntakeStatusBaseSteps]['currentStatus']['actionedBy'];

  const now = Timestamp.now();

  // Initialise base verification status
  return {
    ID_VERIFICATION: {
      updatedAt: now,
      id: 'ID_VERIFICATION',
      type: 'ID_VERIFICATION',
      stepType: 'BASE_STEP',
      currentStatus: {
        status: userData?.identification === 'verified' ? 'COMPLETED' : 'NOT_STARTED',
        metadata: null,
        actionedAt: null,
        actionedBy: defaultActionedBy,
        note: null,
      },
    },
    MEDICARE_VERIFICATION: {
      updatedAt: now,
      id: 'MEDICARE_VERIFICATION',
      type: 'MEDICARE_VERIFICATION',
      stepType: 'BASE_STEP',
      currentStatus: {
        status: hasMedicareCardOrIHI(userData) ? 'PENDING' : 'NOT_STARTED',
        metadata: null,
        actionedAt: null,
        actionedBy: defaultActionedBy,
        note: null,
      },
    },
    UPLOAD_HEALTHCARE_SUMMARY: {
      updatedAt: now,
      id: 'UPLOAD_HEALTHCARE_SUMMARY',
      type: 'UPLOAD_HEALTHCARE_SUMMARY',
      stepType: 'BASE_STEP',
      isDisabled: true,
      currentStatus: {
        status: 'NOT_STARTED',
        metadata: null,
        actionedAt: null,
        actionedBy: defaultActionedBy,
        note: null,
      },
    },
    UPLOAD_REFERRAL_LETTER_OF_SUPPORT: {
      updatedAt: now,
      id: 'UPLOAD_REFERRAL_LETTER_OF_SUPPORT',
      type: 'UPLOAD_REFERRAL_LETTER_OF_SUPPORT',
      stepType: 'BASE_STEP',
      isDisabled: true,
      currentStatus: {
        status: 'NOT_STARTED',
        metadata: null,
        actionedAt: null,
        actionedBy: defaultActionedBy,
        note: null,
      },
    },
    GENERATE_CONSENT_TO_RELEASE_HEALTH_SUMMARY: buildGenerateConsentToReleaseHealthSummaryStep({
      latestHealthSummaryReleaseId: userData?.latestHealthSummaryReleaseId ?? null,
      defaultActionedBy,
    }),
    MHR_ACCESS: {
      updatedAt: now,
      id: 'MHR_ACCESS',
      type: 'MHR_ACCESS',
      stepType: 'BASE_STEP',
      isDisabled: true,
      currentStatus: {
        status: 'NOT_STARTED',
        metadata: null,
        actionedAt: null,
        actionedBy: defaultActionedBy,
        note: null,
      },
    },
    ACCOUNT_VERIFICATION: {
      updatedAt: now,
      id: 'ACCOUNT_VERIFICATION',
      type: 'ACCOUNT_VERIFICATION',
      stepType: 'BASE_STEP',
      currentStatus: {
        status: 'NOT_STARTED',
        metadata: null,
        actionedAt: null,
        actionedBy: defaultActionedBy,
        note: null,
      },
    },
  } satisfies IntakeStatusBaseSteps;
};
